'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

// ─── Data ──────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Business Types', href: '#business-types' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Testimonials', href: '#testimonials' },
];

const STATS = [
  { value: '500+', label: 'Active Stores', icon: '🏪' },
  { value: '1.2M+', label: 'Orders Processed', icon: '📦' },
  { value: '₨ 85Cr+', label: 'Revenue Tracked', icon: '💰' },
  { value: '99.9%', label: 'Uptime SLA', icon: '⚡' },
];

const FEATURES = [
  {
    icon: '🖥️',
    title: 'Smart POS System',
    titleUrdu: 'سمارٹ پوائنٹ آف سیل',
    desc: 'Lightning-fast billing with barcode scanning, customer search, and instant receipt generation. Handles 100+ items per transaction.',
    color: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
  },
  {
    icon: '📦',
    title: 'Inventory Management',
    titleUrdu: 'اسٹاک مینجمنٹ',
    desc: 'Real-time stock tracking with low-stock alerts, expiry date monitoring, and automatic reorder notifications.',
    color: 'from-blue-500 to-indigo-600',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
  },
  {
    icon: '📒',
    title: 'Udhar Khata (Credit)',
    titleUrdu: 'ادھار کھاتہ',
    desc: 'Built-in credit ledger for trusted customers. Track outstanding balances, send payment reminders, and manage credit limits.',
    color: 'from-orange-500 to-red-500',
    bg: 'bg-orange-50',
    border: 'border-orange-100',
  },
  {
    icon: '📊',
    title: 'Analytics Dashboard',
    titleUrdu: 'تجزیاتی ڈیش بورڈ',
    desc: 'Daily/weekly/monthly sales charts, top-selling products, peak hours, and profit margin analysis at a glance.',
    color: 'from-purple-500 to-pink-600',
    bg: 'bg-purple-50',
    border: 'border-purple-100',
  },
  {
    icon: '👥',
    title: 'Customer Management',
    titleUrdu: 'کسٹمر مینجمنٹ',
    desc: 'Build customer profiles, track purchase history, segment loyal buyers, and run targeted promotions.',
    color: 'from-cyan-500 to-blue-500',
    bg: 'bg-cyan-50',
    border: 'border-cyan-100',
  },
  {
    icon: '🚚',
    title: 'Order & Delivery',
    titleUrdu: 'آرڈر اور ڈیلیوری',
    desc: 'Manage walk-in, phone, and online orders from one dashboard. Assign riders and track delivery status in real time.',
    color: 'from-yellow-500 to-orange-500',
    bg: 'bg-yellow-50',
    border: 'border-yellow-100',
  },
  {
    icon: '🏢',
    title: 'Multi-Branch Support',
    titleUrdu: 'کثیر برانچ',
    desc: 'Run 1 store or 50 branches from a single account. Each branch has its own inventory, staff, and reports.',
    color: 'from-teal-500 to-emerald-600',
    bg: 'bg-teal-50',
    border: 'border-teal-100',
  },
  {
    icon: '🔒',
    title: 'Role-Based Access',
    titleUrdu: 'رول بیسڈ ایکسیس',
    desc: 'Owner, manager, cashier, and delivery roles with granular permissions. Know who did what and when.',
    color: 'from-slate-500 to-gray-700',
    bg: 'bg-slate-50',
    border: 'border-slate-100',
  },
];

const BUSINESS_TYPES = [
  {
    icon: '🛒',
    type: 'Kiryana Store',
    typeUrdu: 'کریانہ اسٹور',
    desc: 'Atta, ghee, dal, oil, rice — manage your full grocery catalog with barcode support and bulk pricing.',
    color: 'emerald',
    items: ['Atta & Pulses', 'Oils & Ghee', 'Dairy Products', 'Beverages', 'Sugar & Salt'],
  },
  {
    icon: '🍽️',
    type: 'Restaurant',
    typeUrdu: 'ریسٹورنٹ',
    desc: 'Table orders, delivery management, kitchen display, and daily revenue tracking for food businesses.',
    color: 'orange',
    items: ['Menu Management', 'Table Orders', 'Home Delivery', 'Daily Reports', 'Chef Specials'],
  },
  {
    icon: '🥐',
    type: 'Bakery',
    typeUrdu: 'بیکری',
    desc: 'Fresh product scheduling, daily batch tracking, expiry alerts, and seasonal item management.',
    color: 'yellow',
    items: ['Fresh Breads', 'Cakes & Pastries', 'Biscuits', 'Sweets', 'Daily Batches'],
  },
  {
    icon: '💊',
    type: 'Pharmacy',
    typeUrdu: 'میڈیکل اسٹور',
    desc: 'Medicine expiry tracking, batch numbers, prescription order management, and controlled stock alerts.',
    color: 'blue',
    items: ['Medicine Catalog', 'Expiry Tracking', 'Prescription Orders', 'Vitamins', 'First Aid'],
  },
];

const PLANS = [
  {
    name: 'Starter',
    nameUrdu: 'اسٹارٹر',
    price: '₨ 1,999',
    period: '/month',
    desc: 'Perfect for a single store getting started.',
    features: ['1 Branch', 'POS System', 'Basic Inventory', 'Udhar Khata', '10,000 orders/month', 'Email Support'],
    highlight: false,
    cta: 'Start Free Trial',
  },
  {
    name: 'Professional',
    nameUrdu: 'پروفیشنل',
    price: '₨ 4,999',
    period: '/month',
    desc: 'For growing stores that need more power.',
    features: ['3 Branches', 'Everything in Starter', 'Advanced Analytics', 'Customer Management', 'Delivery Tracking', 'WhatsApp Receipts', 'Priority Support'],
    highlight: true,
    cta: 'Get Started',
    badge: 'Most Popular',
  },
  {
    name: 'Enterprise',
    nameUrdu: 'انٹرپرائز',
    price: 'Custom',
    period: '',
    desc: 'For chains and franchises at any scale.',
    features: ['Unlimited Branches', 'Everything in Pro', 'Super Admin Panel', 'API Access', 'Custom Integrations', 'Dedicated Manager', 'SLA Guarantee'],
    highlight: false,
    cta: 'Contact Sales',
  },
];

const TESTIMONIALS = [
  {
    name: 'Haji Mohammad Arif',
    nameUrdu: 'حاجی محمد عارف',
    store: 'Arif Kiryana Store, Lahore',
    avatar: '👨‍💼',
    stars: 5,
    text: 'Pehle 2 ghante hisaab mein lagte the, ab 20 minute mein sab clear ho jata hai. Udhar khata wali feature ne zindagi badal di.',
    textEn: 'Used to spend 2 hours on accounts daily, now it takes 20 minutes. The credit ledger feature changed my life.',
  },
  {
    name: 'Sana Fatima',
    nameUrdu: 'ثنا فاطمہ',
    store: 'Royal Bakery, Karachi',
    avatar: '👩‍🍳',
    stars: 5,
    text: 'Expiry alerts ne humein hazaron rupay ka nuqsan bachaaya. Fresh products ki tracking ab bahut aasaan hai.',
    textEn: 'Expiry alerts saved us thousands in losses. Tracking fresh products is now effortless.',
  },
  {
    name: 'Dr. Khalid Mehmood',
    nameUrdu: 'ڈاکٹر خالد محمود',
    store: 'Al-Shifa Pharmacy, Islamabad',
    avatar: '👨‍⚕️',
    stars: 5,
    text: '3 branches ek jagah se manage karna mushkil tha. Ab sab kuch ek dashboard par hai. Sales bhi 30% barh gayi.',
    textEn: 'Managing 3 branches was challenging. Now everything is on one dashboard. Sales grew 30% too.',
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; dot: string; badge: string }> = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800' },
  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-800' },
};

// ─── Components ────────────────────────────────────────────────────────────────

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="text-yellow-400 text-sm">★</span>
      ))}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">

      {/* ── NAVBAR ── */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur shadow-md' : 'bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <span className="text-2xl">🛍️</span>
            <div className="leading-tight">
              <span className="text-xl font-extrabold text-emerald-600 tracking-tight">RashanPro</span>
              <span className="hidden sm:inline text-xs text-gray-400 ml-1 font-medium">Management</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/login"
              className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors px-3 py-2"
            >
              Sign In
            </Link>
            <Link
              href="/login"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-all hover:shadow-emerald-200 hover:shadow-lg"
            >
              Start Free Trial
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="text-xl">{mobileMenuOpen ? '✕' : '☰'}</span>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="text-sm font-medium text-gray-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {l.label}
                </a>
              ))}
              <hr className="border-gray-100" />
              <Link href="/login" className="text-sm font-medium text-gray-700">Sign In</Link>
              <Link
                href="/login"
                className="bg-emerald-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl text-center"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900">
        {/* Background grid */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Glow blobs */}
        <div className="absolute top-1/4 -left-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 flex flex-col lg:flex-row items-center gap-16">
          {/* Left text */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              Trusted by 500+ Stores Across Pakistan
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4">
              Run Your Store{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                Smarter
              </span>
              <br />Not Harder
            </h1>

            <p className="text-xl text-white/80 font-urdu text-right mb-2 leading-relaxed">
              اپنا اسٹور، اپنے انداز میں چلائیں
            </p>

            <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              One platform for Kiryana stores, restaurants, bakeries, and pharmacies.
              Inventory, POS, Udhar Khata, and analytics — all in Urdu &amp; English.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <Link
                href="/login"
                className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 py-4 rounded-2xl text-base shadow-lg shadow-emerald-500/30 transition-all hover:scale-105"
              >
                🚀 Start Free — 30 Days
              </Link>
              <a
                href="#features"
                className="w-full sm:w-auto border border-white/20 hover:border-emerald-400 text-white/80 hover:text-white font-semibold px-8 py-4 rounded-2xl text-base transition-all"
              >
                See Features →
              </a>
            </div>

            <p className="mt-4 text-gray-400 text-sm">No credit card required · Setup in 5 minutes</p>
          </div>

          {/* Right — mock dashboard card */}
          <div className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 shadow-2xl">
              {/* Fake topbar */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-white font-bold text-sm">Good Morning, Ali Hassan 👋</p>
                  <p className="text-gray-400 text-xs">Arif Kiryana Store · Today</p>
                </div>
                <span className="bg-emerald-500/20 text-emerald-400 text-xs font-semibold px-3 py-1 rounded-full">Live</span>
              </div>
              {/* Stat cards */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { label: "Today's Sales", value: '₨ 24,800', icon: '📈', change: '+12%', up: true },
                  { label: 'Orders', value: '47', icon: '📦', change: '+5', up: true },
                  { label: 'Low Stock', value: '3 items', icon: '⚠️', change: 'Alert', up: false },
                  { label: 'Udhar Balance', value: '₨ 8,500', icon: '📒', change: '6 customers', up: null },
                ].map((s) => (
                  <div key={s.label} className="bg-white/5 rounded-2xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-lg">{s.icon}</span>
                      <span
                        className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${s.up === true ? 'bg-emerald-500/20 text-emerald-400' :
                          s.up === false ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}
                      >
                        {s.change}
                      </span>
                    </div>
                    <p className="text-white font-bold text-base">{s.value}</p>
                    <p className="text-gray-400 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
              {/* Fake chart bars */}
              <div className="bg-white/5 rounded-2xl p-4">
                <p className="text-gray-400 text-xs mb-3">Weekly Sales (₨)</p>
                <div className="flex items-end gap-2 h-16">
                  {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t-md bg-gradient-to-t from-emerald-600 to-emerald-400"
                        style={{ height: `${h}%` }}
                      />
                      <span className="text-gray-500 text-[9px]">
                        {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl mb-1">{s.icon}</div>
                <div className="text-3xl font-extrabold text-white mb-1">{s.value}</div>
                <div className="text-emerald-100 text-sm font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
              Everything You Need
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Every feature your store needs,<br />
              <span className="text-emerald-600">built for Pakistan</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Designed for shopkeepers who want results — not complexity.
              Available fully in Urdu with offline support.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className={`${f.bg} ${f.border} border rounded-2xl p-6 hover:shadow-lg transition-all hover:-translate-y-1 group`}
              >
                <div
                  className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${f.color} flex items-center justify-center text-2xl mb-4 shadow-sm group-hover:scale-110 transition-transform`}
                >
                  {f.icon}
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-0.5">{f.title}</h3>
                <p className="text-xs text-gray-400 mb-2 text-right font-medium" style={{ fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl' }}>
                  {f.titleUrdu}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BUSINESS TYPES ── */}
      <section id="business-types" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-slate-100 text-slate-600 text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
              Built for Your Business
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              One platform,{' '}
              <span className="text-emerald-600">every type of store</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Whether you sell atta, burgers, bread, or medicine — RashanPro is configured for your exact business type out of the box.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BUSINESS_TYPES.map((b) => {
              const c = colorMap[b.color];
              return (
                <div
                  key={b.type}
                  className={`${c.bg} ${c.border} border-2 rounded-3xl p-6 hover:shadow-xl transition-all hover:-translate-y-1`}
                >
                  <div className="text-5xl mb-4">{b.icon}</div>
                  <h3 className={`text-xl font-extrabold ${c.text} mb-1`}>{b.type}</h3>
                  <p
                    className="text-sm text-gray-500 mb-3 text-right"
                    style={{ fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl' }}
                  >
                    {b.typeUrdu}
                  </p>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{b.desc}</p>
                  <ul className="space-y-1.5">
                    {b.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                        <span className={`w-1.5 h-1.5 rounded-full ${c.dot} flex-shrink-0`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-emerald-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
              Simple Setup
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Live in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                5 minutes
              </span>
            </h2>
            <p className="text-gray-300 text-lg max-w-xl mx-auto">
              No IT team needed. No complicated setup. Just sign up and start selling.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: '01', icon: '📝', title: 'Create Account', desc: 'Sign up free, pick your store type — Kiryana, Restaurant, Bakery, or Pharmacy.' },
              { step: '02', icon: '📦', title: 'Add Your Products', desc: 'Import from our pre-built Pakistan catalog or add custom products in minutes.' },
              { step: '03', icon: '🚀', title: 'Start Selling', desc: 'Open the POS, scan or search products, and print receipts. Simple as that.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="relative inline-block mb-5">
                  <div className="w-20 h-20 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-4xl mx-auto">
                    {s.icon}
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-emerald-500 text-white text-xs font-extrabold rounded-full flex items-center justify-center">
                    {s.step}
                  </span>
                </div>
                <h3 className="font-bold text-lg text-white mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-yellow-100 text-yellow-700 text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
              Customer Stories
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Trusted by shopkeepers{' '}
              <span className="text-emerald-600">across Pakistan</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm hover:shadow-lg transition-all"
              >
                <StarRating count={t.stars} />
                <p className="text-gray-700 text-sm leading-relaxed mt-4 mb-3 italic">
                  &ldquo;{t.textEn}&rdquo;
                </p>
                <p
                  className="text-gray-500 text-sm leading-loose text-right mb-5"
                  style={{ fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl' }}
                >
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-xl">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.store}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
              Transparent Pricing
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Simple, honest pricing —{' '}
              <span className="text-emerald-600">no hidden fees</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Start free for 30 days. No credit card required.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8 items-stretch">
            {PLANS.map((p) => (
              <div
                key={p.name}
                className={`relative rounded-3xl p-8 flex flex-col ${p.highlight
                  ? 'bg-emerald-600 text-white shadow-2xl shadow-emerald-200 ring-4 ring-emerald-500/30 scale-105'
                  : 'bg-white border-2 border-gray-100 text-gray-900'
                  }`}
              >
                {p.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-yellow-400 text-yellow-900 text-xs font-extrabold px-4 py-1.5 rounded-full shadow">
                      {p.badge}
                    </span>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className={`text-xl font-extrabold mb-0.5 ${p.highlight ? 'text-white' : 'text-gray-900'}`}>
                    {p.name}
                  </h3>
                  <p
                    className={`text-sm mb-4 ${p.highlight ? 'text-emerald-200' : 'text-gray-400'}`}
                    style={{ fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl' }}
                  >
                    {p.nameUrdu}
                  </p>
                  <div className="flex items-end gap-1 mb-2">
                    <span className={`text-4xl font-extrabold ${p.highlight ? 'text-white' : 'text-gray-900'}`}>
                      {p.price}
                    </span>
                    {p.period && (
                      <span className={`text-sm pb-1 ${p.highlight ? 'text-emerald-200' : 'text-gray-400'}`}>
                        {p.period}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${p.highlight ? 'text-emerald-100' : 'text-gray-500'}`}>{p.desc}</p>
                </div>

                <ul className="space-y-3 flex-1 mb-8">
                  {p.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-2.5 text-sm">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs ${p.highlight ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-600'
                          }`}
                      >
                        ✓
                      </span>
                      <span className={p.highlight ? 'text-white' : 'text-gray-700'}>{feat}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  href="/login"
                  className={`block text-center font-bold py-3.5 rounded-2xl transition-all ${p.highlight
                    ? 'bg-white text-emerald-600 hover:bg-emerald-50'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                >
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-400 text-sm mt-10">
            All plans include free setup assistance · Cancel anytime · Data export available
          </p>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl mb-6">🛍️</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            Ready to take control of your store?
          </h2>
          <p
            className="text-emerald-100 text-xl mb-8 leading-relaxed"
            style={{ fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl' }}
          >
            آج ہی شروع کریں — 30 دن مفت
          </p>
          <p className="text-emerald-100 text-lg mb-10">
            Join 500+ stores already saving time and growing profits with RashanPro.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/login"
              className="w-full sm:w-auto bg-white text-emerald-600 font-extrabold px-10 py-4 rounded-2xl text-lg shadow-xl hover:shadow-white/30 transition-all hover:scale-105"
            >
              🚀 Start Free Trial
            </Link>
            <a
              href="mailto:support@rashanpro.pk"
              className="w-full sm:w-auto border-2 border-white/40 hover:border-white text-white font-bold px-10 py-4 rounded-2xl text-lg transition-all"
            >
              💬 Talk to Us
            </a>
          </div>
          <p className="text-emerald-200 text-sm mt-6">No credit card required · 5-minute setup · Cancel anytime</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-slate-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🛍️</span>
                <span className="text-xl font-extrabold text-white">RashanPro</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">
                Pakistan&apos;s most trusted multi-store management platform.
                Built for shopkeepers, by shopkeepers.
              </p>
              <p
                className="text-sm text-gray-500 text-right leading-loose"
                style={{ fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl' }}
              >
                پاکستان کا بہترین اسٹور مینجمنٹ سسٹم
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-bold text-sm mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                {['Features', 'Pricing', 'Business Types', 'Mobile App', 'API Access'].map((l) => (
                  <li key={l}>
                    <a href="#" className="hover:text-emerald-400 transition-colors">{l}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Business */}
            <div>
              <h4 className="text-white font-bold text-sm mb-4">Business Types</h4>
              <ul className="space-y-2 text-sm">
                {['Kiryana Store', 'Restaurant', 'Bakery', 'Pharmacy', 'General Store'].map((l) => (
                  <li key={l}>
                    <a href="#" className="hover:text-emerald-400 transition-colors">{l}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-bold text-sm mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                {['Help Center', 'WhatsApp Support', 'Video Tutorials', 'Privacy Policy', 'Terms of Service'].map((l) => (
                  <li key={l}>
                    <a href="#" className="hover:text-emerald-400 transition-colors">{l}</a>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <p className="text-xs text-gray-600 mb-2">Contact</p>
                <a href="mailto:support@rashanpro.pk" className="text-sm text-emerald-400 hover:text-emerald-300">
                  support@rashanpro.pk
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              © 2026 RashanPro. All rights reserved. Made with ❤️ in Pakistan 🇵🇰
            </p>
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="hover:text-emerald-400 transition-colors">Privacy</a>
              <a href="#" className="hover:text-emerald-400 transition-colors">Terms</a>
              <a href="#" className="hover:text-emerald-400 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
